import React from 'react'
import { CookbookQuestionnaire } from '../../types'
import CollapsibleSection from './components/CollapsibleSection'
import CheckboxGroup from './components/CheckboxGroup'
import RadioGroup from './components/RadioGroup'
import RankingInput from './components/RankingInput'
import TextArea from './components/TextArea'
import { AlertCircle, Loader2, Mail } from 'lucide-react'

interface RecipeQuestionnaireStepProps {
  questionnaire: CookbookQuestionnaire
  onChange: (updates: Partial<CookbookQuestionnaire>) => void
  onSubmit: () => void
  isSubmitting: boolean
  error: string | null
}

// Options for various questions
const LIKED_MEALS = [
  'Spaghetti bolognese',
  'Chicken stir-fry with vegetables',
  'Mild chicken curry with rice',
  'Fish pie',
  "Shepherd's pie",
  'Cottage pie',
  'Baked salmon with potatoes and peas',
  'Cod goujons with sweet potato wedges',
  'Tomato and basil pasta',
  'Macaroni cheese (lighter version)',
  'Roast chicken with vegetables',
  'Sausages with mash and peas',
  'Vegetable lasagne',
  'Lentil bolognese',
  'Chicken fajitas (mild)',
  'Beef meatballs with tomato sauce',
  'Turkey or chicken burgers',
  'Tuna pasta bake',
  'Jacket potato with beans and cheese',
  'Homemade pizza with veg toppings',
  'Omelette with vegetables',
  'Bean and cheese quesadillas',
  'Chicken noodle soup',
  'Vegetable and chickpea curry',
  'Sweetcorn fritters with yoghurt dip',
  'Rice bowls (protein + veg + sauce)',
  'Prawn stir-fry',
  'Thai-style chicken rice (very mild)',
  'Falafel wraps',
  'Moroccan-style couscous with chicken or veg'
]

const FISH_OPTIONS = ['Salmon', 'Cod', 'Haddock', 'Tuna']
const SEAFOOD_OPTIONS = ['Prawns', 'Mussels', 'Other shellfish']
const MEAT_OPTIONS = ['Beef mince', 'Lamb mince', 'Chicken', 'Turkey', 'Pork', 'Sausages', 'Meatballs']
const EGG_OPTIONS = ['Scrambled', 'Boiled', 'Fried', 'Omelette', 'Quiche/frittata']
const PLANT_PROTEIN_OPTIONS = ['Lentils', 'Chickpeas', 'Black beans', 'Kidney beans', 'Tofu', 'Tempeh', 'Quorn']
const TEXTURE_OPTIONS = ['Crunchy', 'Soft', 'Mixed textures']
const STRONG_DISLIKES = [
  'Mushrooms',
  'Onions (visible pieces)',
  'Onions (all forms)',
  'Peppers',
  'Courgettes',
  'Aubergine',
  'Peas',
  'Sweetcorn',
  'Broccoli',
  'Spinach',
  'Tomatoes',
  'Chewy textures',
  'Sauces / mixed dishes',
  'Spicy heat',
  'Citrus flavours'
]
const CARB_OPTIONS = ['Pasta', 'Rice', 'Potatoes', 'Noodles', 'Couscous', 'Wraps', 'Bread']
const MEAL_STYLE_OPTIONS = ['Build-your-own meals', 'Prefer plated meals', 'Like dipping sauces']
const ALLERGY_OPTIONS = ['Dairy', 'Gluten', 'Wheat', 'Eggs', 'Peanuts', 'Tree nuts', 'Soya', 'Fish', 'Shellfish', 'Sesame']
const DIETARY_OPTIONS = [
  'Vegetarian',
  'Vegan',
  'Pescatarian',
  'Halal',
  'Kosher',
  'Low sugar',
  'Low salt',
  'No artificial sweeteners',
  'No ultra-processed foods'
]
const NUTRITIONAL_GOALS = ['High protein', 'Low carb', 'High fibre', 'Hidden vegetables']
const NANNY_EXPECTATIONS = [
  'Choose meals proactively from the cookbook',
  'Use ingredients already in the fridge',
  'Stick strictly to the recipes provided',
  'Prepare components (e.g., chop veg, cook rice/pasta)',
  'Cook full meals',
  'Only warm/prep food'
]
const APPLIANCE_OPTIONS = ['Air fryer', 'Slow cooker', 'Oven', 'Microwave', 'Hob', 'Food processor']

export default function RecipeQuestionnaireStep({
  questionnaire,
  onChange,
  onSubmit,
  isSubmitting,
  error
}: RecipeQuestionnaireStepProps) {
  return (
    <div className="space-y-6">
      {/* Introduction */}
      <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
        <h1 className="text-xl font-bold text-gray-900 mb-3">About the questions</h1>
        <p className="text-gray-600 mb-3">
          We know this questionnaire looks long — think of it as a menu of prompts rather than a test. <strong>You can skip anything.</strong> Every question is optional.
        </p>
        <p className="text-gray-600 mb-3">
          We'll use whatever information you choose to give us to tailor your cookbook. If you leave sections blank, we'll rely on what we know works for thousands of families we've supported.
        </p>
        <p className="text-gray-600">
          <strong>The more detail you share, the more precisely your cookbook will reflect your children's needs.</strong>
        </p>
      </div>

      {/* Email (required) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-semibold text-gray-900">Your Email Address</h2>
          <span className="text-red-500">*</span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          We'll send your completed cookbook to this email address. This may take 5-10 minutes.
        </p>
        <input
          type="email"
          value={questionnaire.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="your.email@example.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
          required
        />
      </div>

      {/* Family name */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Family Name</h2>
        <p className="text-sm text-gray-600 mb-4">
          This will be used to personalise your cookbook title.
        </p>
        <input
          type="text"
          value={questionnaire.familyName}
          onChange={(e) => onChange({ familyName: e.target.value })}
          placeholder="e.g., Robinson"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      {/* Children's names and ages */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Children's Names and Ages</h2>
        <p className="text-sm text-gray-600 mb-4">
          This helps us personalise the cookbook and calculate appropriate portion sizes.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Children's names</label>
            <input
              type="text"
              value={questionnaire.childrenNames}
              onChange={(e) => onChange({ childrenNames: e.target.value })}
              placeholder="e.g., Emma and Jack"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Children's ages</label>
            <input
              type="text"
              value={questionnaire.childrenAges}
              onChange={(e) => onChange({ childrenAges: e.target.value })}
              placeholder="e.g., 3 and 6 years old"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Allergies & Dietary Requirements - Moved to top */}
      <CollapsibleSection
        title="Allergies & Dietary Requirements"
        description="Critical safety information"
        defaultOpen={true}
      >
        <div className="space-y-8">
          {/* Allergies */}
          <div className="bg-red-50 rounded-lg p-4 border border-red-100">
            <h3 className="font-medium text-red-800 mb-3">Allergies — tick all that apply</h3>
            <p className="text-sm text-red-700 mb-3">
              These will be strictly avoided in all recipes.
            </p>
            <CheckboxGroup
              options={ALLERGY_OPTIONS}
              selected={questionnaire.allergies}
              onChange={(allergies) => onChange({ allergies })}
              columns={3}
              showSelectAll
            />
            <div className="mt-4">
              <TextArea
                label="Other allergies"
                placeholder="List any other allergies not mentioned above..."
                value={questionnaire.otherAllergies}
                onChange={(otherAllergies) => onChange({ otherAllergies })}
              />
            </div>
          </div>

          {/* Dietary requirements */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Dietary requirements</h3>
            <CheckboxGroup
              options={DIETARY_OPTIONS}
              selected={questionnaire.dietaryRequirements}
              onChange={(dietaryRequirements) => onChange({ dietaryRequirements })}
              columns={3}
              showSelectAll
            />
          </div>

          {/* Medical requirements */}
          <div>
            <TextArea
              label="Medical requirements"
              placeholder="Any medical dietary requirements not covered above..."
              value={questionnaire.medicalRequirements}
              onChange={(medicalRequirements) => onChange({ medicalRequirements })}
            />
          </div>

          {/* Additional notes */}
          <div>
            <TextArea
              label="Anything else to note?"
              placeholder="Any other dietary information..."
              value={questionnaire.dietaryNotes}
              onChange={(dietaryNotes) => onChange({ dietaryNotes })}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 1: Liked Meals */}
      <CollapsibleSection
        title="Section 1 — What meals do your children like?"
        description="Tick as many as you like"
      >
        <CheckboxGroup
          options={LIKED_MEALS}
          selected={questionnaire.likedMeals}
          onChange={(likedMeals) => onChange({ likedMeals })}
          columns={2}
          showSelectAll
        />

        {/* Options for how to use the selected meals */}
        <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
          <h4 className="font-medium text-gray-900 mb-3">What should we do with your selections?</h4>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={questionnaire.includeLikedMeals}
                onChange={(e) => onChange({ includeLikedMeals: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">Include the above meals in the cookbook</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={questionnaire.wantNewIdeas}
                onChange={(e) => onChange({ wantNewIdeas: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">Give us some new ideas based on these preferences</span>
            </label>
          </div>
        </div>

        <div className="mt-4">
          <TextArea
            label="Any other notes on what you've indicated above?"
            value={questionnaire.mealNotes}
            onChange={(mealNotes) => onChange({ mealNotes })}
            placeholder="e.g., Other meals they enjoy, specific preferences..."
          />
        </div>
      </CollapsibleSection>

      {/* Section 2: Food Likes & Dislikes */}
      <CollapsibleSection
        title="Section 2 — General Food Likes & Dislikes"
        description="Tell us about proteins, flavours, textures, and more"
      >
        <div className="space-y-8">
          {/* Fish */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Fish — Do your children enjoy fish?</h3>
            <CheckboxGroup
              options={FISH_OPTIONS}
              selected={questionnaire.fish}
              onChange={(fish) => onChange({ fish })}
              columns={4}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note about fish?"
                value={questionnaire.fishNotes}
                onChange={(fishNotes) => onChange({ fishNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Seafood */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Seafood — Do your children enjoy seafood?</h3>
            <CheckboxGroup
              options={SEAFOOD_OPTIONS}
              selected={questionnaire.seafood}
              onChange={(seafood) => onChange({ seafood })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note about seafood?"
                value={questionnaire.seafoodNotes}
                onChange={(seafoodNotes) => onChange({ seafoodNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Meat */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Meat — Which meats do your children enjoy?</h3>
            <CheckboxGroup
              options={MEAT_OPTIONS}
              selected={questionnaire.meat}
              onChange={(meat) => onChange({ meat })}
              columns={4}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note about meat?"
                value={questionnaire.meatNotes}
                onChange={(meatNotes) => onChange({ meatNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Eggs */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Eggs — Which forms of egg do your children enjoy?</h3>
            <CheckboxGroup
              options={EGG_OPTIONS}
              selected={questionnaire.eggs}
              onChange={(eggs) => onChange({ eggs })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note about eggs?"
                value={questionnaire.eggNotes}
                onChange={(eggNotes) => onChange({ eggNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Plant proteins */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Which plant-based proteins do your children enjoy?</h3>
            <CheckboxGroup
              options={PLANT_PROTEIN_OPTIONS}
              selected={questionnaire.plantProteins}
              onChange={(plantProteins) => onChange({ plantProteins })}
              columns={4}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.plantProteinNotes}
                onChange={(plantProteinNotes) => onChange({ plantProteinNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Flavour strength */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">What flavour strength do your children prefer?</h3>
            <RadioGroup
              name="flavourStrength"
              options={['Mild', 'Medium', 'Stronger flavours']}
              value={questionnaire.flavourStrength}
              onChange={(flavourStrength) => onChange({ flavourStrength })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.flavourNotes}
                onChange={(flavourNotes) => onChange({ flavourNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Spices */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Do your children enjoy spices?</h3>
            <RadioGroup
              name="spices"
              options={['Yes, generally', 'In small amounts', 'Prefer not to use spices']}
              value={questionnaire.spices}
              onChange={(spices) => onChange({ spices })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.spiceNotes}
                onChange={(spiceNotes) => onChange({ spiceNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Herbs */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Do your children enjoy herbs?</h3>
            <RadioGroup
              name="herbs"
              options={['Yes, generally', 'In small amounts', 'Prefer not to use herbs']}
              value={questionnaire.herbs}
              onChange={(herbs) => onChange({ herbs })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.herbNotes}
                onChange={(herbNotes) => onChange({ herbNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Textures */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">What textures do your children prefer?</h3>
            <CheckboxGroup
              options={TEXTURE_OPTIONS}
              selected={questionnaire.textures}
              onChange={(textures) => onChange({ textures })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.textureNotes}
                onChange={(textureNotes) => onChange({ textureNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Strong dislikes */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Strong dislikes — tick all that apply</h3>
            <CheckboxGroup
              options={STRONG_DISLIKES}
              selected={questionnaire.strongDislikes}
              onChange={(strongDislikes) => onChange({ strongDislikes })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.dislikeNotes}
                onChange={(dislikeNotes) => onChange({ dislikeNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Carbs */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Which carbohydrate sources do your children enjoy?</h3>
            <CheckboxGroup
              options={CARB_OPTIONS}
              selected={questionnaire.carbs}
              onChange={(carbs) => onChange({ carbs })}
              columns={4}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.carbNotes}
                onChange={(carbNotes) => onChange({ carbNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Meal styles */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Which meal styles work well?</h3>
            <CheckboxGroup
              options={MEAL_STYLE_OPTIONS}
              selected={questionnaire.mealStyles}
              onChange={(mealStyles) => onChange({ mealStyles })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.mealStyleNotes}
                onChange={(mealStyleNotes) => onChange({ mealStyleNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Packed lunch */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Is a packed lunch relevant?</h3>
            <RadioGroup
              name="packedLunch"
              options={['Yes', 'No']}
              value={questionnaire.packedLunch ? 'Yes' : 'No'}
              onChange={(val) => onChange({ packedLunch: val === 'Yes' })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.packedLunchNotes}
                onChange={(packedLunchNotes) => onChange({ packedLunchNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* After-school snacks */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Should the cookbook include after-school snacks?</h3>
            <RadioGroup
              name="afterSchoolSnacks"
              options={['Yes', 'No']}
              value={questionnaire.afterSchoolSnacks ? 'Yes' : 'No'}
              onChange={(val) => onChange({ afterSchoolSnacks: val === 'Yes' })}
            />
          </div>

          {/* Children help cook */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Do you want recipes that children can help cook after school?</h3>
            <RadioGroup
              name="childrenHelpCook"
              options={['Yes, often', 'Yes, sometimes', 'No']}
              value={questionnaire.childrenHelpCook}
              onChange={(childrenHelpCook) => onChange({ childrenHelpCook })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.cookingHelpNotes}
                onChange={(cookingHelpNotes) => onChange({ cookingHelpNotes })}
                rows={1}
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 3: Nutritional Preferences */}
      <CollapsibleSection
        title="Section 3 — Nutritional Preferences & Household Food Rules"
        description="Help us prioritise what matters most"
      >
        <div className="space-y-8">
          {/* Nutritional ranking */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Nutritional goals — rank 1–4</h3>
            <RankingInput
              items={NUTRITIONAL_GOALS}
              ranking={questionnaire.nutritionalRanking}
              onChange={(nutritionalRanking) => onChange({ nutritionalRanking })}
            />
            <div className="mt-4">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.nutritionalNotes}
                onChange={(nutritionalNotes) => onChange({ nutritionalNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Pudding */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Should children have pudding with dinner?</h3>
            <RadioGroup
              name="pudding"
              options={['Yes, every day', 'Yes, occasionally', 'No']}
              value={questionnaire.pudding}
              onChange={(pudding) => onChange({ pudding })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.puddingNotes}
                onChange={(puddingNotes) => onChange({ puddingNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Family food rules */}
          <div>
            <TextArea
              label="Family food rules"
              placeholder="e.g., No sweets on weekdays, must finish vegetables before seconds..."
              value={questionnaire.familyFoodRules}
              onChange={(familyFoodRules) => onChange({ familyFoodRules })}
              rows={3}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 5: Household Constraints */}
      <CollapsibleSection
        title="Section 4 — Parent & Household Constraints"
        description="Practical details about cooking in your home"
      >
        <div className="space-y-8">
          {/* Dinner time */}
          <div>
            <TextArea
              label="What time should the children have dinner?"
              placeholder="e.g., 5:30pm"
              value={questionnaire.dinnerTime}
              onChange={(dinnerTime) => onChange({ dinnerTime })}
              rows={1}
            />
          </div>

          {/* Cooking duration */}
          <div>
            <TextArea
              label="How long can the nanny spend cooking?"
              placeholder="e.g., 30 minutes, 45 minutes"
              value={questionnaire.cookingDuration}
              onChange={(cookingDuration) => onChange({ cookingDuration })}
              rows={1}
            />
          </div>

          {/* Nanny confidence */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">How confident is your nanny with cooking?</h3>
            <RadioGroup
              name="nannyConfidence"
              options={[
                'Beginner — needs simple steps and clear guidance',
                'Intermediate — comfortable with everyday meals',
                'Confident — can improvise and follow more complex recipes',
                "Don't know"
              ]}
              value={questionnaire.nannyConfidence}
              onChange={(nannyConfidence) => onChange({ nannyConfidence })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.nannyConfidenceNotes}
                onChange={(nannyConfidenceNotes) => onChange({ nannyConfidenceNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Nanny expectations */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">What do you expect from your nanny when it comes to cooking?</h3>
            <CheckboxGroup
              options={NANNY_EXPECTATIONS}
              selected={questionnaire.nannyExpectations}
              onChange={(nannyExpectations) => onChange({ nannyExpectations })}
              columns={2}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.nannyExpectationNotes}
                onChange={(nannyExpectationNotes) => onChange({ nannyExpectationNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Batch cooking */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Do you want the nanny to batch cook?</h3>
            <RadioGroup
              name="batchCook"
              options={['Yes', 'No', 'Sometimes']}
              value={questionnaire.batchCook}
              onChange={(batchCook) => onChange({ batchCook })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.batchCookNotes}
                onChange={(batchCookNotes) => onChange({ batchCookNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Weekday dinners */}
          <div>
            <TextArea
              label="How many weekday dinners are expected?"
              placeholder="e.g., 5 dinners per week"
              value={questionnaire.weekdayDinners}
              onChange={(weekdayDinners) => onChange({ weekdayDinners })}
              rows={1}
            />
          </div>

          {/* Leftovers */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Should the nanny cook extra for leftovers?</h3>
            <p className="text-sm text-gray-600 mb-3">
              This means cooking slightly more than needed so there's food left over for a subsequent meal.
            </p>
            <RadioGroup
              name="leftovers"
              options={['Yes', 'No', 'Occasionally']}
              value={questionnaire.leftovers}
              onChange={(leftovers) => onChange({ leftovers })}
            />
            <div className="mt-2">
              <TextArea
                placeholder="Anything else to note?"
                value={questionnaire.leftoverNotes}
                onChange={(leftoverNotes) => onChange({ leftoverNotes })}
                rows={1}
              />
            </div>
          </div>

          {/* Appliances */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">What appliances are available?</h3>
            <CheckboxGroup
              options={APPLIANCE_OPTIONS}
              selected={questionnaire.appliances}
              onChange={(appliances) => onChange({ appliances })}
              columns={3}
              showSelectAll
            />
            <div className="mt-2">
              <TextArea
                placeholder="Other appliances..."
                value={questionnaire.otherAppliances}
                onChange={(otherAppliances) => onChange({ otherAppliances })}
                rows={1}
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Submit section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Thank you so much for all your info!</h2>
        <p className="text-gray-600 mb-4">
          And now we're going to create your book.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-800 text-sm">
            <strong>Please note:</strong> Creating your personalised cookbook may take a little time. The system is matching your children's tastes, routines, and dietary needs with the best recipe options.
          </p>
          <p className="text-amber-800 text-sm mt-2">
            It's worth the wait — once it's ready, you'll receive an email with a complete, customised set of meals designed specifically for your family.
          </p>
        </div>
        <button
          onClick={onSubmit}
          disabled={isSubmitting || !questionnaire.email}
          className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center gap-2 ${
            isSubmitting || !questionnaire.email
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating your cookbook...
            </>
          ) : (
            'Create My Cookbook'
          )}
        </button>
      </div>
    </div>
  )
}
